import { useEffect, useRef, useState, RefObject } from 'react'

export interface UseIntersectionObserverOptions {
  threshold?: number | number[]
  root?: Element | null
  rootMargin?: string
  enabled?: boolean
  triggerOnce?: boolean
}

export interface UseIntersectionObserverReturn<T = HTMLDivElement> {
  ref: RefObject<T>
  isIntersecting: boolean
  entry?: IntersectionObserverEntry
}

export const useIntersectionObserver = <
  T extends HTMLElement = HTMLDivElement
>({
  threshold = 0,
  root = null,
  rootMargin = '0px',
  enabled = true,
  triggerOnce = false,
}: UseIntersectionObserverOptions = {}): UseIntersectionObserverReturn<T> => {
  const ref = useRef<T>(null)
  const [entry, setEntry] = useState<IntersectionObserverEntry>()
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    if (!enabled || !ref.current) {
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[useIntersectionObserver] IntersectionObserver is not supported in this browser. Falling back to visible state.'
      )
      setIsIntersecting(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setEntry(entry)
        setIsIntersecting(entry.isIntersecting)

        if (triggerOnce && entry.isIntersecting) {
          observer.disconnect()
        }
      },
      {
        threshold,
        root,
        rootMargin,
      }
    )

    const element = ref.current
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [enabled, threshold, root, rootMargin, triggerOnce])

  return { ref, isIntersecting, entry }
}

export default useIntersectionObserver
